// Import necessary dependencies for session management and React context
import { useSessionQuery } from '@/useQuery/queries/session';
import { createContext, useContext, ReactNode, useEffect, useState, useRef } from 'react';
import { SessionResponse } from '@/types/api/api-v2';
import { getCookie, setCookie } from 'cookies-next';
import cookiesConstants from '@/constants/cookies.constants';
import useEvents from '@/hooks/useEvents';
import useHasMounted from '@/hooks/useHasMounted';

// Define the type for the session context, extending SessionResponse with optional sessionStartTime
type SessionContextType = SessionResponse | null;

// Create the session context with default values
const SessionContext = createContext<SessionContextType>({
  sessionToken: null,
  sessionId: '',
  isAnonymous: false,
  expires: '',
  cookies: [],
  sessionStartTime: 0,
});

// Define props interface for the SessionProvider component
interface SessionProviderProps {
  children: ReactNode;
  sessionToken: string | null;
}

// SessionProvider component that manages session state and cookie handling
export const SessionProvider = ({ children }: SessionProviderProps) => {
  // State to track initialization and session start time
  const hasMounted = useHasMounted();
  const isInitializedRef = useRef(false);
  const [sessionStartTime, setSessionStartTime] = useState<number>(0);
  // Fetch session data using the provided sessionToken
  const { data: session } = useSessionQuery();

  const { fireSessionStartEvent } = useEvents('', []);

  const sessionToken = getCookie(cookiesConstants.COOKIES.SESSION_TOKEN.name);

  useEffect(() => {
    if (hasMounted && !sessionToken) {
      fireSessionStartEvent();
    }
  }, [sessionToken, fireSessionStartEvent, hasMounted]);

  useEffect(() => {
    // Mark the provider as initialized
    isInitializedRef.current = true;

    if (session) {
      // Set session start time when session is established
      if (!sessionStartTime) {
        setSessionStartTime(Date.now());
      }

      // Process session cookies if they exist
      if (session.cookies && session.cookies.length > 0) {
        session.cookies.forEach(cookie => {
          // Only process session token cookies
          if (cookie.startsWith('sessionToken=')) {
            // Extract the token value from the cookie string
            const tokenValue = cookie.split('=')[1].split(';')[0];
            const cookieOptions: { [key: string]: any } = {};

            // Parse cookie attributes from the cookie string
            const cookieParts = cookie.split(';');
            for (let i = 1; i < cookieParts.length; i++) {
              const part = cookieParts[`${i}`].trim();
              if (part.includes('=')) {
                // Handle key-value pairs in cookie attributes
                const [key, value] = part.split('=');
                const keyLower = key.toLowerCase();

                // Special handling for different cookie attributes
                if (keyLower === 'expires') {
                  try {
                    cookieOptions[`${keyLower}`] = new Date(value);
                  } catch (e: unknown) {
                    console.error('Invalid expires date in cookie:', value, e);
                  }
                } else if (keyLower === 'max-age') {
                  cookieOptions[`${keyLower}`] = parseInt(value, 10);
                } else {
                  cookieOptions[`${keyLower}`] = value;
                }
              } else {
                // Handle boolean flags in cookie attributes
                const flagName = part.toLowerCase();
                if (flagName === 'secure') {
                  cookieOptions.secure = true;
                } else if (flagName === 'samesite') {
                  cookieOptions.sameSite = true;
                } else {
                  cookieOptions[`${flagName}`] = true;
                }
              }
            }

            // Set default path if not specified
            if (!cookieOptions.path) {
              cookieOptions.path = '/';
            }

            // Set the session token cookie with parsed options
            setCookie(cookiesConstants.COOKIES.SESSION_TOKEN.name, tokenValue, cookieOptions);
          }
        });
      }
    }
  }, [session, sessionStartTime]);

  // Provide the session context to children components
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
};

// Custom hook to access the session context
export const useSession = () => {
  const context = useContext(SessionContext);
  // Return default values if context is not available
  return context || { sessionToken: null, sessionStartTime: undefined };
};
